import React from 'react';
import { styled, makeStyles } from '@mui/styles';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import theme from '../../core/theme';
import { ContentResponse } from '../../data/types/content.interface';
import { capitalize, compact, last } from 'lodash';
import { TagType } from '../../data/types/tags.interface';

const useStyles = makeStyles({
  hover: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  overflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '90%',
  },
});

interface Props {
  content: ContentResponse;
  tagType?: string;
  index?: number;
  showTheme?: boolean;
  onRowClicked: () => void;
}

const StyledTableCell: any = styled(TableCell)(() => ({
  paddingTop: '12px',
  paddingBottom: '12px',
  borderBottom: `1px solid ${theme.palette.chipBg.main}`,
}));

const ContentRowForTag: React.FC<Props> = ({ index, content, tagType, showTheme, onRowClicked }) => {
  const classes = useStyles();

  const contentList: TagType[] = compact(content?.themes?.filter((theme) => theme.type === tagType)) || [];

  const getColumns = () => {
    return (
      <>
        {contentList.length > 0 && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{contentList.map((data) => data?.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <Tooltip title={contentList.map((data) => data?.name).join(', ')}>
                <div className={classes.overflow}>{contentList.map((data) => data?.name).join(', ')}</div>
              </Tooltip>
            </StyledTableCell>
          </>
        )}
      </>
    );
  };

  const isThemeTopicOrSubTopic = () => {
    if (tagType) {
      return (
        ((tagType === 'theme' || tagType === 'topic' || tagType === 'sub-topic') && contentList.length > 0) ||
        !['theme', 'topic', 'sub-topic'].includes(tagType)
      );
    }
  };

  return (
    <>
      <TableRow key={content.id} onClick={onRowClicked}>
        {content?.brands && tagType === 'brand' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.brands?.map((brand) => brand.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.brands?.map((brand) => brand.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.personalities && tagType === 'person' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.personalities?.map((person) => person.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.personalities?.map((person) => person.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.identity_tags && tagType === 'identity' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.identity_tags?.map((identity) => identity.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.identity_tags?.map((identity) => identity.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.location_tags && tagType === 'location' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.location_tags?.map((location) => location.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.location_tags?.map((location) => location.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.event_tags && tagType === 'event' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.event_tags?.map((event) => event.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.event_tags?.map((event) => event.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.authors && tagType === 'author' && (
          <>
            <StyledTableCell component="td" style={{ width: '6%' }}>
              <div>{content?.authors?.map((author) => author.id).join(' , ')}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '12%' }} role="button" className={classes.hover}>
              <div>{content?.authors?.map((author) => author.name).join(' , ')}</div>
            </StyledTableCell>
          </>
        )}
        {content?.themes && (tagType === 'theme' || tagType === 'topic' || tagType === 'sub-topic') && getColumns()}

        {tagType && content.assetContentType && isThemeTopicOrSubTopic() && (
          <StyledTableCell component="td" style={{ width: '10%' }} role="button" className={classes.hover}>
            <div>{capitalize(content.assetContentType.name)}</div>
          </StyledTableCell>
        )}

        {index && showTheme && (
          <StyledTableCell component="td" style={{ width: '5%' }} role="button" className={classes.hover}>
            <div>{index}</div>
          </StyledTableCell>
        )}

        {((tagType && isThemeTopicOrSubTopic()) || showTheme) && (
          <>
            <StyledTableCell component="td" style={{ width: '20%' }} role="button" className={classes.hover}>
              <div className={classes.overflow}>{last(content?.pathLink?.split('/')) || ''}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '7%' }} role="button" className={classes.hover}>
              <div>{capitalize(content?.type)}</div>
            </StyledTableCell>
            <StyledTableCell component="td" role="button" style={{ width: '20%' }} className={classes.hover}>
              <div className={classes.overflow}>{content.filename}</div>
            </StyledTableCell>
            {showTheme && (
              <>
                <StyledTableCell component="td" role="button" style={{ width: '15%' }} className={classes.hover}>
                  <div className={classes.overflow}>{content.title}</div>
                </StyledTableCell>
                <StyledTableCell component="td" style={{ width: '8%' }} role="button" className={classes.hover}>
                  <div>{content?.theme}</div>
                </StyledTableCell>
              </>
            )}
            <StyledTableCell component="td" style={{ width: '10%' }} role="button" className={classes.hover}>
              <div>{content?.category}</div>
            </StyledTableCell>
            <StyledTableCell component="td" style={{ width: '15%' }} role="button" className={classes.hover}>
              <div>{content.subCategory}</div>
            </StyledTableCell>
          </>
        )}
      </TableRow>
    </>
  );
};

export default ContentRowForTag;
